import { LOCALE_STORAGE_KEY } from "./localeConstants";
import { DOMESTIC_LOCALE, SUPPORTED_LANGUAGE_CODES } from "./languages";

export const DEFAULT_LANGUAGE = DOMESTIC_LOCALE;

function isSupportedLanguage(code) {
  return SUPPORTED_LANGUAGE_CODES.includes(code);
}

/**
 * ISO 3166-1 alpha-2 country → site language.
 * 未收录国家默认走英文（外贸站更友好）。
 */
const COUNTRY_TO_LANGUAGE = {
  CN: "zh",
  TW: "zh-hant",
  HK: "zh-hant",
  MO: "zh-hant",

  US: "en",
  GB: "en",
  AU: "en",
  NZ: "en",
  CA: "en",
  IE: "en",
  SG: "en",
  MY: "en",
  PH: "en",
  ZA: "en",

  DE: "de",
  AT: "de",
  LI: "de",
  CH: "de",

  BR: "pt",
  PT: "pt",
  AO: "pt",
  MZ: "pt",

  ES: "es",
  MX: "es",
  AR: "es",
  CO: "es",
  CL: "es",
  PE: "es",
  VE: "es",
  EC: "es",
  UY: "es",
  PY: "es",
  BO: "es",
  CR: "es",
  PA: "es",
  GT: "es",
  DO: "es",

  FR: "fr",
  BE: "fr",
  LU: "fr",
  MC: "fr",

  IT: "it",
  SM: "it",
  VA: "it",

  IN: "hi",
  RU: "ru",
  BY: "ru",
  KZ: "ru",
  KG: "ru",
  UZ: "ru",

  KR: "ko",
  JP: "ja",
  TH: "th",
  TR: "tr",
  VN: "vi",
  NL: "nl",
  ID: "id",

  SA: "ar",
  AE: "ar",
  EG: "ar",
  QA: "ar",
  KW: "ar",
  BH: "ar",
  OM: "ar",
  JO: "ar",
  LB: "ar",
  MA: "ar",
  DZ: "ar",
  TN: "ar",
  IQ: "ar",

  PL: "pl",
};

export function languageFromCountryCode(countryCode) {
  if (!countryCode) return null;
  const code = String(countryCode).trim().toUpperCase();
  const language = COUNTRY_TO_LANGUAGE[code];
  return language && isSupportedLanguage(language) ? language : null;
}

function detectFromBrowser() {
  const full = (navigator.language || "").toLowerCase();
  if (
    full.startsWith("zh-tw") ||
    full.startsWith("zh-hk") ||
    full.startsWith("zh-mo") ||
    full === "zh-hant"
  ) {
    return "zh-hant";
  }
  if (full === "zh-cn" || full === "zh" || full.startsWith("zh-hans")) {
    return "zh";
  }
  const primary = full.split("-")[0];
  if (primary && isSupportedLanguage(primary)) {
    return primary;
  }
  return null;
}

function readStoredLocale() {
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !isSupportedLanguage(parsed.language)) return null;
    return parsed;
  } catch {
    return null;
  }
}

async function fetchJsonWithTimeout(url, timeoutMs = 2500) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      signal: controller.signal,
      cache: "no-store",
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

async function fetchTextWithTimeout(url, timeoutMs = 2500) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      signal: controller.signal,
      cache: "no-store",
    });
    if (!response.ok) return "";
    return (await response.text()).trim();
  } catch {
    return "";
  } finally {
    window.clearTimeout(timer);
  }
}

/** 多个免费 GeoIP 源，提高国内/海外可达性 */
export async function fetchVisitorCountryCode() {
  const sources = [
    async () => {
      const data = await fetchJsonWithTimeout("https://ipapi.co/json/");
      return data?.country_code || data?.country || "";
    },
    async () => {
      const data = await fetchJsonWithTimeout("https://ip.useragentinfo.com/json");
      return data?.country || data?.short_name || "";
    },
    async () => {
      const text = await fetchTextWithTimeout("https://ipapi.co/country/");
      return /^[A-Za-z]{2}$/.test(text) ? text : "";
    },
  ];

  for (const source of sources) {
    try {
      const country = await source();
      if (country && typeof country === "string" && country.length >= 2) {
        return country.slice(0, 2).toUpperCase();
      }
    } catch {
      // try next
    }
  }
  return "";
}

/**
 * 解析访客应使用的语言：
 * 1. 用户手动选过的语言（最高优先）
 * 2. IP 所在国家
 * 3. 浏览器语言
 * 4. 站点默认
 */
export async function resolvePreferredLanguage() {
  const stored = readStoredLocale();
  if (stored?.preferenceSource === "manual" && isSupportedLanguage(stored.language)) {
    return stored.language;
  }

  const country = await fetchVisitorCountryCode();
  const fromGeo = languageFromCountryCode(country);
  if (fromGeo) {
    return fromGeo;
  }

  // 未映射国家：默认英文；中国区相关已在表中覆盖
  if (country && !fromGeo) {
    return "en";
  }

  const fromBrowser = detectFromBrowser();
  if (fromBrowser) {
    return fromBrowser;
  }

  if (stored?.language && isSupportedLanguage(stored.language)) {
    return stored.language;
  }

  return DEFAULT_LANGUAGE;
}

/** 同步回退：仅手动偏好 / 浏览器（用于无法 await 的场景） */
export function detectPreferredLanguageSync() {
  const stored = readStoredLocale();
  if (stored?.preferenceSource === "manual" && isSupportedLanguage(stored.language)) {
    return stored.language;
  }
  const fromBrowser = detectFromBrowser();
  if (fromBrowser) return fromBrowser;
  if (stored?.language && isSupportedLanguage(stored.language)) {
    return stored.language;
  }
  return DEFAULT_LANGUAGE;
}
